import React, { useEffect, useRef, useState } from 'react';
import Button from '../../components/Button/Button';
import { motion } from 'framer-motion';
import Background from '../../components/Background/Background';
import Header from '../../components/Header/Header';
import { planeVariants } from '../../animations/plane';
import fadeIn from '../../animations/fadeIn';
import Logo from '../../components/Logo/Logo';
import { TextField } from '@mui/material';
import useQuery from '../../hooks/useQuery';
import { sendPasswordResetCode, verifyAccount } from '../../api/resetPassword';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal/Modal';

const PasswordCode = () => {
  const query = useQuery();
  const [email, setEmail] = useState(query.get('email') as string);
  const [emailSent, setEmailSent] = useState(false);
  const [code, setCode] = useState('');
  const [changeEmail, setChangeEmail] = useState(false);
  const navigate = useNavigate();

  const pin1 = useRef<any>();
  const pin2 = useRef<any>();
  const pin3 = useRef<any>();
  const pin4 = useRef<any>();

  useEffect(() => {
    if (!emailSent) {
      if (email) {
        sendPasswordResetCode(email);
      }
      setEmailSent(true);
    }

    return () => {};
  }, [email, emailSent]);

  const onPinChange = (position: number, e: any) => {
    setCode(
      `${pin1.current.value}${pin2.current.value}${pin3.current.value}${pin4.current.value}`
    );
    if (e.target.value.length === 0) {
      return;
    }
    switch (position) {
      case 1:
        pin2.current.focus();
        break;
      case 2:
        pin3.current.focus();
        break;
      case 3:
        pin4.current.focus();
        break;
      default:
        pin4.current.blur();
        break;
    }
  };

  const continueToReset = async () => {
    const data = await verifyAccount(email, code);
    if (data.success) {
      navigate(`/resetpassword?code=${code}&email=${email}`);
    }
  };

  return (
    <>
      <Modal visible={changeEmail} onClose={() => setChangeEmail(false)}>
        <p className='text-primary font-bold'>Oh no!</p>
        <p className='text-xs'>
          We do not have access to your email address, please enter your email
          address to proceed.
        </p>
        <TextField
          id='standard-basic'
          label='Email Address'
          variant='standard'
          className='w-full'
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          onClick={() => navigate(`/resetcode?email=${email}`)}
          delay={-0.5}
        >
          Let's Try Again
        </Button>
      </Modal>
      <Background image='login-bg'>
        <Header title='Reset Password' />
        <motion.img
          initial='hidden'
          animate='enter'
          exit='exit'
          src='/plane.png'
          className='signin-plane'
          variants={planeVariants}
        />
        <div className='auth-container'>
          <motion.div
            variants={fadeIn(0.5)}
            initial='hidden'
            animate='enter'
            exit='exit'
            className='auth-logo'
          >
            <Logo name='awayz' className='max-w-[100%]' />
          </motion.div>
          <div className='w-full flex flex-col gap-6 items-center'>
            <motion.p
              variants={fadeIn(0.45)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='w-full text-xl font-semibold'
            >
              Password recovery
            </motion.p>
            <div className='flex justify-between items-end w-full'>
              <div className='flex-1 flex flex-col justify-end'>
                <motion.p
                  variants={fadeIn(0.3)}
                  initial='hidden'
                  animate='enter'
                  exit='exit'
                  className='w-full text-xs'
                >
                  Enter the 4 digit number sent to:
                </motion.p>
                <span className='text-primary mt-1 text-xs'>{email}</span>
              </div>
              <div
                onClick={() => setChangeEmail(true)}
                className='cursor-pointer'
              >
                <motion.p
                  variants={fadeIn(0.3)}
                  initial='hidden'
                  animate='enter'
                  exit='exit'
                  className='w-full text-xs text-right underline'
                >
                  CHANGE EMAIL
                </motion.p>
              </div>
            </div>
            <div className='flex gap-4'>
              <motion.div
                variants={fadeIn(0.15)}
                initial='hidden'
                animate='enter'
                exit='exit'
                className='w-full'
              >
                <TextField
                  id='standard-basic'
                  variant='standard'
                  className='w-full text-center text-3xl'
                  placeholder='0'
                  inputRef={pin1}
                  onChange={(e) => onPinChange(1, e)}
                  type='number'
                  inputProps={{
                    min: 0,
                    style: { textAlign: 'center', fontSize: '3rem' },
                  }}
                />
              </motion.div>
              <motion.div
                variants={fadeIn(0.15)}
                initial='hidden'
                animate='enter'
                exit='exit'
                className='w-full'
              >
                <TextField
                  id='standard-basic'
                  variant='standard'
                  className='w-full'
                  placeholder='0'
                  inputRef={pin2}
                  onChange={(e) => onPinChange(2, e)}
                  type='number'
                  inputProps={{
                    min: 0,
                    style: { textAlign: 'center', fontSize: '3rem' },
                  }}
                />
              </motion.div>
              <motion.div
                variants={fadeIn(0.15)}
                initial='hidden'
                animate='enter'
                exit='exit'
                className='w-full'
              >
                <TextField
                  id='standard-basic'
                  variant='standard'
                  className='w-full'
                  placeholder='0'
                  inputRef={pin3}
                  onChange={(e) => onPinChange(3, e)}
                  type='number'
                  inputProps={{
                    min: 0,
                    style: { textAlign: 'center', fontSize: '3rem' },
                  }}
                />
              </motion.div>
              <motion.div
                variants={fadeIn(0.15)}
                initial='hidden'
                animate='enter'
                exit='exit'
                className='w-full'
              >
                <TextField
                  id='standard-basic'
                  variant='standard'
                  className='w-full'
                  placeholder='0'
                  inputRef={pin4}
                  onChange={(e) => onPinChange(4, e)}
                  type='number'
                  inputProps={{
                    min: 0,
                    style: { textAlign: 'center', fontSize: '3rem' },
                  }}
                />
              </motion.div>
            </div>
            <motion.p
              variants={fadeIn(0.3)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='w-full text-xs text-center underline'
              onClick={() => setEmailSent(false)}
            >
              RESEND CODE
            </motion.p>
            <Button delay={0.05} onClick={continueToReset}>
              Continue
            </Button>
          </div>
        </div>
      </Background>
    </>
  );
};

export default PasswordCode;
